'use client'

import { useState, useCallback, useRef } from 'react'
import cn from 'classnames'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import DiscountBox from '@booking/components/DiscountBox'
import ErrorBox from '@booking/components/ErrorBox'

import PaymentForm from './components/PaymentForm'
import PaymentMethods from './components/PaymentMethods'
import MbWayForm from './components/MbWayForm'
import FreeContinue from './components/FreeContinue'
import ProtectSection from './components/ProtectSection'
import TermsSection from './components/TermsSection'
import LockedPayment from './components/LockedPayment'

import ReduniqImage from './reduniq.png'
import { usePaymentFlow } from './hooks/usePaymentFlow'
import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import { createCart, setAddons } from '../../services/api'
import { Cart } from '../../types/booking'

// ---------------------------------------------------------------------------

type ProtectChoice = 'yes' | 'no'

export default function PaymentProcess() {
  const t = useTranslations('booking')

  const { cart, config } = useBookingState()
  const { rebuildCart, dispatch } = useBookingActionsCtx()

  const allowDiscountCode = config.allowDiscountCode ?? true
  const showProtect = config.paymentSections?.protect ?? false
  const showTerms = config.paymentSections?.terms !== false // default on

  // ---------------------------------------------------------------------------
  // Payment flow

  const {
    state,
    method,
    hasFired,
    setHasFired,
    actionUrl,
    showPaymentMethods,
    showFreeContinue,
    onPayWithCard,
    onPayWithPaypal,
    onPayWithMbWay,
    onDiscountChanged,
    onFreeContinue,
    shouldRebuildBeforePay,
    afterRebuildCart,
  } = usePaymentFlow({ rebuildCart })

  // ---------------------------------------------------------------------------
  // Section state

  const [protectChoice, setProtectChoice] = useState<ProtectChoice | null>(null)
  const [showProtectError, setShowProtectError] = useState(false)
  const protectChoiceRef = useRef<ProtectChoice | null>(null)
  const protectRequestInFlight = useRef(false)

  const [sessionAccepted, setSessionAccepted] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsError, setShowTermsError] = useState(false)

  // Progressive unlock: each section must be completed before the next appears
  const protectDone = !showProtect || protectChoice !== null
  const termsDone = !showTerms || /*sessionAccepted && */ termsAccepted
  const paymentUnlocked = protectDone && termsDone

  const handleProtectChoice = useCallback(
    async (choice: ProtectChoice) => {
      // Ignore repeat clicks on the already-selected option, and ignore
      // clicks while a previous choice is still being submitted.
      if (protectChoiceRef.current === choice || protectRequestInFlight.current) return

      protectChoiceRef.current = choice
      protectRequestInFlight.current = true
      setProtectChoice(choice)
      setShowProtectError(false)

      try {
        const shouldRebuild = shouldRebuildBeforePay()
        const useCart = shouldRebuild ? ((await rebuildCart()) as Cart) : cart
        if (shouldRebuild) afterRebuildCart()
        if (!useCart) return

        const updatedCart = await setAddons(useCart.id, [
          {
            category: 'protect',
            selected: choice === 'yes',
          },
        ])

        dispatch({
          type: 'SET_CART',
          cart: updatedCart,
        })

        dispatch({
          type: 'SET_SELECTED_ADDONS',
          selectedAddons: choice === 'yes' ? ['insurance'] : [],
        })
      } finally {
        protectRequestInFlight.current = false
      }
    },
    [dispatch, shouldRebuildBeforePay, cart, rebuildCart, afterRebuildCart],
  )

  const handleSessionToggle = useCallback(() => setSessionAccepted((v) => !v), [])
  const handleTermsToggle = useCallback(() => {
    setTermsAccepted((v) => !v)
    setShowTermsError(false)
  }, [])

  const handleDiscountChanged = useCallback(() => {
    onDiscountChanged()
  }, [onDiscountChanged])

  // ---------------------------------------------------------------------------
  // Locked payment message

  const lockedMessage = !protectDone
    ? t('ticket.payment.locked.waitProtect')
    : t('ticket.payment.locked.waitTerms')

  // ---------------------------------------------------------------------------

  return (
    <div className={cn(shared.box)}>
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          {state.error ? <ErrorBox error={state.error} /> : null}

          <div className={styles.payment}>
            <BackButton />

            {allowDiscountCode ? (
              <h2 className={styles.mainTitle}>{t('ticket.payment.title')}</h2>
            ) : null}

            <SummaryBox simple className={cn(styles.purchaseDetails)} />

            {allowDiscountCode ? (
              <DiscountBox hasFired={hasFired} onApplyOrRemove={handleDiscountChanged} />
            ) : null}

            {/* ── Protect section ── */}
            {showProtect && (
              <ProtectSection
                choice={protectChoice}
                onChoose={handleProtectChoice}
                showError={showProtectError}
              />
            )}

            {/* ── Terms section (unlocks after protect) ── */}
            {protectDone && showTerms && (
              <TermsSection
                sessionAccepted={sessionAccepted}
                termsAccepted={termsAccepted}
                onSessionToggle={handleSessionToggle}
                onTermsToggle={handleTermsToggle}
              />
            )}

            {/* ── Payment methods or locked placeholder ── */}
            {paymentUnlocked ? (
              <>
                {showPaymentMethods ? (
                  <>
                    <h2 className={styles.chooseMethodTitle}>
                      <UnlockIcon />
                      {t('ticket.payment.chooseMethod')}
                    </h2>
                    <PaymentMethods
                      method={method}
                      onPayWithCard={onPayWithCard}
                      onPayWithPaypal={onPayWithPaypal}
                      onPayWithMbWay={onPayWithMbWay}
                    />
                  </>
                ) : null}
              </>
            ) : (
              <LockedPayment title={t('ticket.payment.locked.title')} message={lockedMessage} />
            )}

            {method === 'card' && state.cart ? <PaymentForm action={actionUrl} /> : null}

            {method === 'mbway' ? (
              <MbWayForm hasFired={hasFired} onFire={() => setHasFired(true)} />
            ) : null}

            {showFreeContinue ? <FreeContinue onContinue={onFreeContinue} /> : null}
          </div>
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox className={cn(styles.summaryBox)} />
          <HelpBox />
          <Image src={ReduniqImage} alt="" className={cn(styles.reduniq)} />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function UnlockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M7 11V7a5 5 0 0 1 9.9-1"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}
